import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { pairwise, startWith } from 'rxjs/operators';
import { BullhornFieldType, CriteriaData, Entities, IOperator, Operators, TimeOperator, UnitsOfTime } from '../data-rules-model';
import { DataRulesService } from '../data-rules.service';
import { AutocompleteComponent } from '../lib/autocomplete/autocomplete.component';
import { ChipListAutocompleteComponent, IChipItem } from '../lib/chip-list-autocomplete/chip-list-autocomplete.component';
import { ICustomPositioningConfig } from '../lib/custom-component-overlay.directive';
import { DynamicOptionsProvider, StaticOptionsProvider } from '../lib/option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../lib/paired-autocomplete/paired-autocomplete.component';
import { ProviderFactory } from '../lib/provider-factory';
import { IFieldMeta, MetaDataService, ToManyMeta } from '../meta-data.service';
import { AddressCriteriaComponent } from './address-criteria/address-criteria.component';
import { CompareCriteriaComponent } from './compare-criteria/compare-criteria.component';
import { SearchPeriod } from './searchPeriod';

@Component({
  selector: 'app-criteria',
  templateUrl: './criteria.component.html',
  imports: [
    PairedAutocompleteComponent,
    ReactiveFormsModule,
    NgIf,
    MatSelect,
    MatOption,
    NgForOf,
    ChipListAutocompleteComponent,
    MatChipGrid,
    MatChipRow,
    MatIcon,
    MatChipInput,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    AutocompleteComponent,
    AddressCriteriaComponent,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatChipRemove,
    MatButtonToggleGroup,
    MatButtonToggle,
    CompareCriteriaComponent,
    MatTooltip
  ],
  styleUrls: ['./criteria.component.scss']
})
export class CriteriaComponent implements OnInit, AfterViewInit {

  @ViewChild('fieldValue')
  set fieldValue(v: AutocompleteComponent | ChipListAutocompleteComponent) {
    setTimeout(() => {
      if (
        this.fieldTypeControl.value === 'TO_MANY'
        && (this.operatorControl.value === 'in' || this.operatorControl.value === 'notIn')
      ) {
        this.setChipListValuePickerComponent(v as ChipListAutocompleteComponent);
      } else {
        this.setValuePickerComponent(v as AutocompleteComponent);
      }
    }, 0);
  }

  private static isToOneOrToMany(fieldMeta: IFieldMeta): boolean {
    return fieldMeta.metaType === 'TO_MANY' || fieldMeta.metaType === 'TO_ONE';
  }

  @Input() criteria: CriteriaData;    // this should not be here as the form is the model of truth
  @Input() formGroup: FormGroup;
  @Input() primaryEntity: Entities;
  fieldMetaData: IFieldMeta[] = [];
  operators: IOperator[];
  unitsOfTime = [];
  unitsTime = UnitsOfTime;
  unitsOfTimeSelected: FormControl;
  timeOperators = [];
  timeOperator = TimeOperator;
  timeOperatorsSelected: FormControl;
  notWithinValue: FormControl;
  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };
  selectedFieldType: string;
  protected selectedFieldToOneType: string | null = null;
  isCompareFieldMode = false;
  valuesReady = false;
  toManyPlaceholderText: string;
  secondaryDescriptorControl: FormControl;
  fieldNameControl: FormControl;
  valueControl: FormControl;
  entitiesOptions: IEntityItem[];
  protected fieldTypeControl: AbstractControl;
  private operatorControl: AbstractControl;
  private searchPeriod: SearchPeriod;
  @ViewChild('fieldName')
  private fieldPickerComponent: PairedAutocompleteComponent;
  private valuePickerComponent: AutocompleteComponent;
  private chipValuePickerComponent: ChipListAutocompleteComponent;
  protected readonly DataRulesService = DataRulesService;

  constructor(
      private metaDataService: MetaDataService,
      private providerFactory: ProviderFactory
  ) {
  }

  ngOnInit(): void {
    this.createControls();
    // initialize unit of time and time operators for within and not within operator
    this.unitsOfTime = Object.keys(UnitsOfTime);
    this.timeOperators = Object.keys(TimeOperator);
    // provide entities options list
    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity).subscribe(
      opts => {
        this.entitiesOptions = opts.sort((a, b) => {
          const isACustomObject = a.value.includes('CustomObject');
          const isBCustomObject = b.value.includes('CustomObject');
          const isAPrimary = this.primaryEntity === a.value;
          const isBPrimary = this.primaryEntity === b.value;
          if ((isACustomObject && !isBCustomObject)
            || isBPrimary) {
            return 1;
          } else if ((!isACustomObject && isBCustomObject)
            || isAPrimary) {
            return -1;
          } else {
            return 0;
          }
        });
        console.log('this.entitiesOptions: ', this.entitiesOptions);
      }
      , error => {
        console.error(error);
      });

    if (this.criteria.fieldType === BullhornFieldType.FIELD) {
      this.isCompareFieldMode = true;
    }

    // force initialisation of operators
    this.fieldNameChange(this.criteria.fieldName);

    // initialise value control
    this.valueControl.setValue(this.criteria.value);

    // allow appropriate template control to be used
    this.valuesReady = this.criteria.operator === 'in' || this.criteria.operator === 'notIn';

    this.subscribeToChanges();
    this.searchPeriod = new SearchPeriod();
    if (this.formGroup.get('operator').value === 'within'
      || this.formGroup.get('operator').value === 'notWithin') {
      console.warn(this.valueControl.value);
      if (!this.valueControl.value) {
        this.searchPeriod = new SearchPeriod();
      } else {
        this.searchPeriod = this.valueControl.value as SearchPeriod;
        this.unitsOfTimeSelected.setValue(this.searchPeriod.unitTime);
        this.timeOperatorsSelected.setValue(this.searchPeriod.timeOperator);
        this.notWithinValue.setValue(this.searchPeriod.operatorValue);
      }
    }

  }

  private matchSelectedValuesToItems(items: ToManyMeta[]): IChipItem[] {
    // force the order of the items to match the order of the values - otherwise deleting items will
    // delete the wrong one - we should fix this properly
    const selItems = [];
    this.valueControl.value.forEach(id => {
      const data = items.find(d => d.id === id);
      selItems.push({id: data.id, name: data.name});
    });
    return selItems;
  }

  ngAfterViewInit() {
    this.secondaryDescriptorChange(this.criteria.secondaryDescriptor, true);
  }

  addInValue(event: MatChipInputEvent): void {
    if (event.value) {
      this.valueControl.setValue([...this.valueControl.value, event.value]);
      event.input.value = '';
      this.valueControl.markAsDirty();
    }
  }

  removeInValue(val: string): void {
    const values = this.valueControl.value;
    const index = values.indexOf(val);
    if (index >= 0) {
      values.splice(index, 1);
      this.valueControl.setValue(values);
      this.valueControl.markAsDirty();
    }
  }

  private createControls(): void {
    // the form is recreated so we need to remove the old controls
    // otherwise the form and the UI elements get out of sync
    this.formGroup.removeControl('secondaryDescriptor');
    this.formGroup.removeControl('fieldName');
    this.formGroup.removeControl('compositeFieldName');
    this.formGroup.removeControl('fieldType');
    this.formGroup.removeControl('operator');
    this.formGroup.removeControl('value');

    this.secondaryDescriptorControl = new FormControl(this.criteria.secondaryDescriptor);
    this.fieldNameControl = new FormControl(this.criteria.fieldName, Validators.required);
    this.fieldTypeControl = new FormControl(this.criteria.fieldType);
    this.operatorControl = new FormControl(this.criteria.operator);
    this.valueControl = new FormControl(this.criteria.value, Validators.required);

    this.formGroup.removeControl('unitOfTime');
    this.formGroup.removeControl('timeOperator');
    this.formGroup.removeControl('notWithinValue');

    this.unitsOfTimeSelected = new FormControl();
    this.timeOperatorsSelected = new FormControl();
    this.notWithinValue = new FormControl();

    this.formGroup.addControl('unitOfTime', this.unitsOfTimeSelected);
    this.formGroup.addControl('timeOperator', this.timeOperatorsSelected);
    this.formGroup.addControl('notWithinValue', this.notWithinValue);

    this.formGroup.addControl('secondaryDescriptor', this.secondaryDescriptorControl);
    this.formGroup.addControl('fieldName', this.fieldNameControl);
    this.formGroup.addControl('fieldType', this.fieldTypeControl);
    this.formGroup.addControl('operator', this.operatorControl);
    this.formGroup.addControl('value', this.valueControl);
  }

  private subscribeToChanges(): void {
    this.operatorControl.valueChanges.pipe(
      startWith(this.operatorControl.value as string),
      pairwise()
    ).subscribe(([oldOp, newOp]) => this.operatorSelectionChange(oldOp, newOp));

    this.fieldNameControl.valueChanges.subscribe(val => this.fieldNameChange(val));
    this.secondaryDescriptorControl.valueChanges.subscribe(val => {
      this.secondaryDescriptorChange(val);
    });

    this.unitsOfTimeSelected.valueChanges.subscribe(value => this.unitOfTimeChange(value));
    this.timeOperatorsSelected.valueChanges.subscribe(value => this.timeOperatorChange(value));
    this.notWithinValue.valueChanges.subscribe(value => this.notWithinValueChange(value));
  }

  // events for not within and within
  private timeOperatorChange(val: TimeOperator): void {
    this.searchPeriod.timeOperator = val;
    this.valueControl.setValue(this.searchPeriod);
  }

  private notWithinValueChange(val: number): void {
    this.searchPeriod.operatorValue = val;
    this.valueControl.setValue(this.searchPeriod);
  }

  private unitOfTimeChange(val: UnitsOfTime): void {
    this.searchPeriod.unitTime = val;
    this.valueControl.setValue(this.searchPeriod);
  }

  // this will only be called with a "defined" (i.e. not undefined) component when a TO_ONE or TO_MANY field is selected
  private setValuePickerComponent(acComponent: AutocompleteComponent): void {
    this.chipValuePickerComponent = null;
    this.valuePickerComponent = acComponent;

    const fieldName = this.fieldNameControl.value;
    if (this.valuePickerComponent && fieldName) {
      const meta = this.fieldMetaData.find(fm => fm.fieldName === fieldName);
      if (CriteriaComponent.isToOneOrToMany(meta)) {
        this.toManyPlaceholderText = 'Loading...';
        this.valueControl.disable();

        this.providerFactory.getOptionsProvider(this.valueControl.value, meta).subscribe(provider => {
          this.valuePickerComponent.setOptionsProvider(provider, this.valueControl.value);
          this.toManyPlaceholderText = undefined;
          this.valueControl.enable();
        });
      }
    }
  }

  private setChipListValuePickerComponent(cacComponent: ChipListAutocompleteComponent): void {
    this.valuePickerComponent = null;
    this.chipValuePickerComponent = cacComponent;
    const fieldName = this.fieldNameControl.value;

    if (this.chipValuePickerComponent && fieldName) {
      const fieldMeta = this.fieldMetaData.find(meta => {
        return meta.fieldName === fieldName;
      });

      this.resetChipItemsProvider(fieldMeta);

    }
  }

  clearFieldName() {
    this.selectedFieldType = '';
  }

  // affect on entity-list select event
  private secondaryDescriptorChange(descriptor: string | null, initial?: boolean): void {
    this.metaDataService.filterMetaBySecondaryDescriptor(this.primaryEntity, descriptor).subscribe(filteredMeta => {

      // TODO Discuss a MetaData restructuring to avoid the mutation of the data on the frontend side (KENG-108)
      if (this.primaryEntity === Entities.CANDIDATE && descriptor === null) {
        // for case of primaryEntity being 'Candidate' and primary entity is picked in a entity-picker (descriptor is null)
        this.fieldMetaData = filteredMeta.filter(data => {
          const isCustomObject = data.fieldName.includes('customObject');
          return !isCustomObject;
        });
      } else {
        this.fieldMetaData = filteredMeta as IFieldMeta[];
      }

      const optionsProvider = new StaticOptionsProvider(this.fieldMetaData.map(
        fm => ({name: fm.fieldName, label: fm.fieldName.includes('customObject') ? fm.associatedEntity.label : fm.label}
        )));

      this.fieldPickerComponent.setOptionsProvider(optionsProvider, initial ? this.criteria.fieldName : null);

      if (!initial) {
        this.fieldNameControl.setValue('');
        this.fieldPickerComponent.clearOption();
        this.clearFieldName();
      }
    }, error => {
      console.error(error);
    });
  }

  private fieldNameChange(fieldName: string): void {
    this.metaDataService
      .filterMetaBySecondaryDescriptor(this.primaryEntity, this.secondaryDescriptorControl.value)
      .subscribe(filteredMeta => {
        this.fieldMetaData = filteredMeta;
        if (!fieldName || fieldName.length === 0) {
          setTimeout(() => {
            this.formGroup.addControl('value', this.valueControl);
            this.valueControl.setValue(this.getDefaultValue());
          }, 0);
          if (this.valuePickerComponent)
            this.valuePickerComponent.clearOption();
          if (this.chipValuePickerComponent)
            this.chipValuePickerComponent.clearItems();
          return;
        }

        const meta = this.fieldMetaData.find(fm => fm.fieldName === fieldName);
        this.selectedFieldType = meta.metaType;
        this.selectedFieldToOneType = meta.toOneType ?? null;
        this.valueControl.setValue(this.getDefaultValue());
        if (CriteriaComponent.isToOneOrToMany(meta))
          this.lookUpEntityChange(meta);
        this.fieldTypeControl.patchValue(this.isCompareFieldMode ? BullhornFieldType.FIELD : meta.metaType);

        if (this.isCompareFieldMode) {
          this.operators = Operators.getCompareFieldOperators(this.selectedFieldType);
          if (!this.operators.find(op => op.value === this.operatorControl.value)) {
            this.operatorControl.setValue(Operators.Equals.value);
          }
        } else {
          this.populateOperators(fieldName);
        }
        this.applyValueControlValidators();
        this.checkForNullValuesInInput();
      });
  }

  private getDefaultValue(): any {
    if (this.isCompareFieldMode) {
      return null;
    }
    if (this.operatorControl.value === 'in' || this.operatorControl.value === 'notIn' )
      return [];
    if (this.selectedFieldType === 'BOOLEAN')
      return false;
    if (this.selectedFieldType === 'STRING')
      return '';
    if (this.selectedFieldType === 'TO_MANY')
      return '';
    return null;
  }

  private checkForNullValuesInInput() {

    const noInputRequiredOperators =
      this.operatorControl.value === Operators.isNull.value
      || this.operatorControl.value === Operators.isNotNull.value
      || this.operatorControl.value === Operators.HasSome.value
      || this.operatorControl.value === Operators.HasNone.value;

    if (!noInputRequiredOperators && this.valueControl.value === null) {
      this.valueControl.setErrors({invalid: true});
    }
  }

  private lookUpEntityChange(fieldMeta: IFieldMeta): void {
    if (this.valuePickerComponent) {
      const optionsProvider = new DynamicOptionsProvider(this.metaDataService, fieldMeta);
      this.valuePickerComponent.setOptionsProvider(optionsProvider);
    } else if (this.chipValuePickerComponent) {
      this.resetChipItemsProvider(fieldMeta);
    }
  }

  private applyValueControlValidators(): void {
    // BH Search cannot search empty strings on Candidate or Note so
    // text criteria fields must have a value while Query entities do not need a value
    const isSearchEntityAndString =
        DataRulesService.isSelectedEntitySearchEntity(this.primaryEntity) && this.selectedFieldType === 'STRING';
    const isBooleanOrDate =
      this.selectedFieldType === BullhornFieldType.BOOLEAN
      || this.selectedFieldType === BullhornFieldType.DATE;
    const isOperatorNullLike =
      this.operatorControl.value === Operators.isNull.value
      || this.operatorControl.value === Operators.isNotNull.value;
    const isHasSomeNone = this.operatorControl.value === Operators.HasNone.value
      || this.operatorControl.value === Operators.HasSome.value;

    // error state hack - previous found errors are stale from prev validators
    this.valueControl.setErrors(null);

    const shouldSetValidators = !(!isSearchEntityAndString || isBooleanOrDate || isOperatorNullLike || isHasSomeNone);
    this.valueControl.setValidators(shouldSetValidators ? Validators.required : null);
    this.valueControl.updateValueAndValidity();
  }

  private populateOperators(fieldName: string): void {
    const meta = this.fieldMetaData.find(fm => fm.fieldName === fieldName);
    this.operators = Operators.getOperators(meta);
    const currentOperator = this.operatorControl.value;
    if (!this.operators.find(op => op.value === currentOperator))
      this.operatorControl.setValue(this.operators[0].value);
  }

  private operatorSelectionChange(oldOperator: any, newOperator: any): void {
    console.log('operatorSelectionChange', oldOperator, newOperator);
    const newOperatorIsNullType = newOperator === 'null' || newOperator === 'notNull';
    if (newOperatorIsNullType) {
      this.switchToValue(null);
    } else {
      this.nonNullOperatorSelectionChange(oldOperator, newOperator);
    }
    this.applyValueControlValidators();
    this.checkForNullValuesInInput();
  }

  private nonNullOperatorSelectionChange(oldOperator: any, newOperator: any): void {
    const oldOperatorIsWithinType = oldOperator === 'within' || oldOperator === 'notWithin';
    const newOperatorIsWithinType = newOperator === 'within' || newOperator === 'notWithin';
    const isSwitchingToOrFromWithinType = oldOperatorIsWithinType !== newOperatorIsWithinType;
    if (isSwitchingToOrFromWithinType) {
      this.switchToValue(null);
      return;
    } else {
      this.handleArrayTypeChange(oldOperator, newOperator);
    }
  }

  private handleArrayTypeChange(oldOperator: any, newOperator: any): void {
    const oldOperatorIsArrayType = oldOperator === 'in' || oldOperator === 'notIn';
    const newOperatorIsArrayType = newOperator === 'in' || newOperator === 'notIn';
    const isSwitchingFromArrayToArray = oldOperatorIsArrayType === newOperatorIsArrayType;
    if (!isSwitchingFromArrayToArray) {
      if (newOperatorIsArrayType) {
        this.switchToArrayValueFromPrimitiveValue();
      } else {
        this.switchToPrimitiveValueFromArrayValue();
      }
    }
  }

  private switchToArrayValueFromPrimitiveValue(): void {
    const currentValue = this.valueControl.value;
    const newValue = currentValue ? [currentValue] : [];
    this.valueControl.setValue(newValue);
    this.valuesReady = true;
    if (this.fieldTypeControl.value === 'TO_MANY' &&
        (this.operatorControl.value === 'in' || this.operatorControl.value === 'notIn')) {
      this.setChipListValuePickerComponent(this.fieldValue as ChipListAutocompleteComponent);
    } else {
      this.setValuePickerComponent(this.fieldValue as AutocompleteComponent);
    }
  }

  private switchToPrimitiveValueFromArrayValue(): void {
    const currentValue = this.valueControl.value;
    const newValue = currentValue.length === 1 ? currentValue[0] : this.getDefaultValue();
    this.switchToValue(newValue);
  }

  private switchToValue(value: any): void {
    this.valuesReady = false;
    this.valueControl.setValue(value);
    this.setValuePickerComponent(this.fieldValue as AutocompleteComponent);
  }

  private resetChipItemsProvider(fieldMeta: IFieldMeta): void {
    this.toManyPlaceholderText = 'Loading...';
    this.valueControl.disable();
    this.chipValuePickerComponent.loading();

    this.providerFactory.getItemsProvider(this.valueControl.value, fieldMeta).subscribe(([provider, selItems]) => {
      this.chipValuePickerComponent.setItemsProvider(provider, selItems);
      this.chipValuePickerComponent.loadComplete();
      this.toManyPlaceholderText = undefined;
      this.valueControl.enable();
    });
  }

  protected canCompareField(): boolean {
    if (DataRulesService.isSelectedEntitySearchEntity(this.primaryEntity)) {
      return false;
    }
    const comparableTypes: BullhornFieldType[] = [
      BullhornFieldType.STRING, BullhornFieldType.INTEGER,
      BullhornFieldType.DOUBLE, BullhornFieldType.FLOAT,
      BullhornFieldType.BIG_DECIMAL, BullhornFieldType.DATE,
      BullhornFieldType.BOOLEAN, BullhornFieldType.TO_ONE
    ];
    return comparableTypes.includes(this.selectedFieldType as BullhornFieldType);
  }

  toggleCompareFieldMode(mode: string): void {
    if (mode === BullhornFieldType.FIELD) {
      this.isCompareFieldMode = true;
      this.operators = Operators.getCompareFieldOperators(this.selectedFieldType);
      this.operatorControl.setValue(Operators.Equals.value);
      this.fieldTypeControl.patchValue(BullhornFieldType.FIELD);
      this.valueControl.setValue(null);
      this.valueControl.setErrors(null);
      this.valueControl.clearValidators();
    } else {
      this.isCompareFieldMode = false;
      this.fieldTypeControl.patchValue(this.selectedFieldType);
      this.populateOperators(this.fieldNameControl.value);
      this.valueControl.setValue(this.getDefaultValue());
      this.applyValueControlValidators();
    }
  }

  onCompareFieldSelected(value: string | null): void {
    this.valueControl.setValue(value);
    if (value) {
      this.valueControl.setErrors(null);
    } else {
      this.valueControl.setErrors({required: true});
    }
  }

  protected isTruncated(element: HTMLElement): boolean {
    const range = document.createRange();
    range.selectNodeContents(element);
    const textWidth = range.getBoundingClientRect().width;
    const containerWidth = element.getBoundingClientRect().width;
    return textWidth > containerWidth + 0.5;
  }
}
